import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as React from "react";

import { cn } from "@/lib/utils";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

type PopoverContentProps = React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> & {
  /**
   * Render the popover content inline instead of in a Portal at the
   * document body. Use this when the popover lives inside another
   * Radix overlay (e.g. a `Dialog`) — portalled content becomes a DOM
   * sibling of the dialog, which makes the parent dialog's focus
   * scope steal focus back from the popover (so inputs can't be
   * typed) and makes `modal` popovers slap `aria-hidden` on the
   * dialog content. Keeping the popover in-tree avoids both.
   */
  disablePortal?: boolean;
};

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    { className, align = "center", sideOffset = 4, disablePortal, ...props },
    ref,
  ) => {
    const content = (
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        /**
         * Fix for issue with scrolling:
         * @see https://github.com/shadcn-ui/ui/issues/607#issuecomment-2481290015
         */
        onWheel={(e) => {
          e.stopPropagation();

          props.onWheel?.(e);
        }}
        onTouchMove={(e) => {
          e.stopPropagation();

          props.onTouchMove?.(e);
        }}
        className={cn(
          "z-[10010] w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-popover-content-transform-origin]",
          className,
        )}
        {...props}
        onInteractOutside={(e) => {
          props.onInteractOutside?.(e);

          if (
            (e.target as HTMLElement)?.id === "sentry-feedback" ||
            (e.target as HTMLElement)?.id === "report-a-bug"
          ) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }}
      />
    );

    if (disablePortal) return content;
    return <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal>;
  },
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
